import * as THREE from 'three';
import { GameObject } from '../objects/GameObject';

/**
 * Type of bounding volume
 */
export enum BoundingVolumeType {
  SPHERE,
  BOX
}

/**
 * Interface for bounding volumes used in collision detection
 */
export interface BoundingVolume {
  /**
   * Get the type of bounding volume
   */
  getType(): BoundingVolumeType;
  
  /**
   * Check if this bounding volume collides with another
   */
  intersects(other: BoundingVolume): boolean;
  
  /**
   * Update the bounding volume based on the owner's position
   */
  update(position: THREE.Vector3): void;
  
  /**
   * Get the position of this bounding volume
   */
  getPosition(): THREE.Vector3;
  
  /**
   * Get a clone of the size (radius for sphere, half-extents for box)
   */
  getSize(): THREE.Vector3 | number;
}

/**
 * Bounding sphere implementation
 */
export class BoundingSphere implements BoundingVolume {
  private position: THREE.Vector3;
  private radius: number;
  
  constructor(radius: number, initialPosition: THREE.Vector3 = new THREE.Vector3()) {
    this.radius = radius;
    this.position = initialPosition.clone();
  }
  
  public getType(): BoundingVolumeType {
    return BoundingVolumeType.SPHERE;
  }
  
  public intersects(other: BoundingVolume): boolean {
    if (other.getType() === BoundingVolumeType.SPHERE) {
      // Sphere-sphere collision
      const otherPosition = other.getPosition();
      const otherRadius = other.getSize() as number;
      
      const distance = this.position.distanceTo(otherPosition);
      return distance < (this.radius + otherRadius);
    } else {
      // Sphere-box collision
      const otherPosition = other.getPosition();
      const otherHalfExtents = other.getSize() as THREE.Vector3;
      
      // Find closest point on the box to the sphere center
      const closestPoint = new THREE.Vector3().copy(this.position);
      
      // Constrain the sphere center to the box extents
      closestPoint.x = Math.max(otherPosition.x - otherHalfExtents.x, 
                       Math.min(otherPosition.x + otherHalfExtents.x, closestPoint.x));
      closestPoint.y = Math.max(otherPosition.y - otherHalfExtents.y, 
                       Math.min(otherPosition.y + otherHalfExtents.y, closestPoint.y));
      closestPoint.z = Math.max(otherPosition.z - otherHalfExtents.z, 
                       Math.min(otherPosition.z + otherHalfExtents.z, closestPoint.z));
      
      // If distance from closest point to sphere center is less than radius, there's a collision
      const distance = this.position.distanceTo(closestPoint);
      return distance < this.radius;
    }
  }
  
  public update(position: THREE.Vector3): void {
    this.position.copy(position);
  }
  
  public getPosition(): THREE.Vector3 {
    return this.position.clone();
  }
  
  public getSize(): number {
    return this.radius;
  }
}

/**
 * Bounding box implementation
 */
export class BoundingBox implements BoundingVolume {
  private position: THREE.Vector3;
  private halfExtents: THREE.Vector3;
  
  constructor(halfExtents: THREE.Vector3, initialPosition: THREE.Vector3 = new THREE.Vector3()) {
    this.halfExtents = halfExtents.clone();
    this.position = initialPosition.clone();
  }
  
  public getType(): BoundingVolumeType {
    return BoundingVolumeType.BOX;
  }
  
  public intersects(other: BoundingVolume): boolean {
    if (other.getType() === BoundingVolumeType.BOX) {
      // Box-box collision
      const otherPosition = other.getPosition();
      const otherHalfExtents = other.getSize() as THREE.Vector3;
      
      // Check each axis for overlap
      return (Math.abs(this.position.x - otherPosition.x) < (this.halfExtents.x + otherHalfExtents.x)) &&
             (Math.abs(this.position.y - otherPosition.y) < (this.halfExtents.y + otherHalfExtents.y)) &&
             (Math.abs(this.position.z - otherPosition.z) < (this.halfExtents.z + otherHalfExtents.z));
    } else {
      // Box-sphere collision (delegate to sphere implementation)
      return other.intersects(this);
    }
  }
  
  public update(position: THREE.Vector3): void {
    this.position.copy(position);
  }
  
  public getPosition(): THREE.Vector3 {
    return this.position.clone();
  }
  
  public getSize(): THREE.Vector3 {
    return this.halfExtents.clone();
  }
} 